export class SuccessFactorsCertificateParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SuccessFactorsCertificateParseError";
  }
}

export type ParsedSuccessFactorsCertificateFile = {
  certificatePem: string;
  privateKeyPem: string;
  companyId: string | null;
};

const PEM_BLOCK_PATTERN = /-----BEGIN ([^-]+)-----([\s\S]*?)-----END \1-----/g;

const COMPANY_ID_SUFFIX_PATTERN = /^SFCPART[A-Za-z0-9]+$/;

const PASSPHRASE_PROTECTED_KEY_MESSAGE =
  "This private key is password-protected. Certn cannot use a passphrase-protected key. In SAP, download or export an unencrypted certificate file and upload that file here.";

function normalizePemBody(body: string): string {
  return body.replace(/\s/g, "");
}

function decodeBase64ToUtf8(base64: string): string | null {
  try {
    const binary = atob(base64);
    return binary;
  } catch {
    return null;
  }
}

function isPkcsBase64(text: string): boolean {
  const normalized = normalizePemBody(text);
  return /^MII[A-Za-z0-9+/=]+$/.test(normalized);
}

function hasUnsupportedControlCharacters(text: string): boolean {
  return [...text].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 8 || (code >= 14 && code <= 31);
  });
}

function isEncryptedPkcs8Base64(base64: string): boolean {
  try {
    const bytes = Uint8Array.from(atob(base64), (character) =>
      character.charCodeAt(0)
    );
    if (bytes[0] !== 0x30 || bytes.length < 3) {
      return false;
    }
    const lengthByte = bytes[1];
    const lengthByteCount = lengthByte & 0x7f;
    if (lengthByte >= 0x80 && (lengthByteCount === 0 || lengthByteCount > 4)) {
      return false;
    }
    const contentOffset = lengthByte < 0x80 ? 2 : 2 + lengthByteCount;
    return contentOffset < bytes.length && bytes[contentOffset] === 0x30;
  } catch {
    return false;
  }
}

function splitSapSuffix(decoded: string): {
  innerBase64: string;
  suffix: string | null;
} | null {
  const suffixMatch = decoded.match(/^(.*?)(###([A-Za-z0-9]+))$/);
  if (suffixMatch) {
    const inner = normalizePemBody(suffixMatch[1]);
    if (isPkcsBase64(inner)) {
      return { innerBase64: inner, suffix: suffixMatch[3] };
    }
    return null;
  }
  const innerOnly = normalizePemBody(decoded);
  if (isPkcsBase64(innerOnly)) {
    return { innerBase64: innerOnly, suffix: null };
  }
  return null;
}

function tryUnwrapSapDoubleBase64(outerBase64: string) {
  const decoded = decodeBase64ToUtf8(outerBase64);
  if (!decoded) {
    return null;
  }
  if (!hasUnsupportedControlCharacters(decoded)) {
    return splitSapSuffix(decoded);
  }
  return null;
}

function buildPem(label: string, base64Body: string): string {
  const wrapped = base64Body.replace(/(.{64})/g, "$1\n").trim();
  return `-----BEGIN ${label}-----\n${wrapped}\n-----END ${label}-----`;
}

function companyIdFromSuffix(suffix: string | null): string | null {
  if (!suffix || suffix === "false") {
    return null;
  }
  if (!COMPANY_ID_SUFFIX_PATTERN.test(suffix)) {
    return null;
  }
  return suffix;
}

export function normalizeSuccessFactorsCertificatePem(pem: string): string {
  const trimmed = pem.trim();
  if (!trimmed) {
    return pem;
  }
  const match = trimmed.match(
    /-----BEGIN CERTIFICATE-----([\s\S]*?)-----END CERTIFICATE-----/
  );
  if (!match) {
    return pem;
  }
  try {
    return resolveCertificatePem("CERTIFICATE", match[1]).pem;
  } catch {
    return pem;
  }
}

export function normalizeSuccessFactorsPrivateKeyPem(pem: string): string {
  const trimmed = pem.trim();
  if (!trimmed) {
    return pem;
  }
  const match = trimmed.match(
    /-----BEGIN (PRIVATE KEY|RSA PRIVATE KEY|ENCRYPTED PRIVATE KEY)-----([\s\S]*?)-----END \1-----/
  );
  if (!match) {
    return pem;
  }
  try {
    return resolvePrivateKeyPem(match[1], match[2]).pem;
  } catch {
    return pem;
  }
}

function isSapWrappedPemBody(pem: string): boolean {
  const match = pem.match(/-----BEGIN [^-]+-----([\s\S]*?)-----END [^-]+-----/);
  if (!match) {
    return false;
  }
  const body = normalizePemBody(match[1]);
  if (isPkcsBase64(body)) {
    return false;
  }
  return tryUnwrapSapDoubleBase64(body) !== null;
}

export function isSapWrappedSuccessFactorsPem(pem: string): boolean {
  const trimmed = pem.trim();
  if (!trimmed) {
    return false;
  }
  return isSapWrappedPemBody(trimmed);
}

function resolveCertificatePem(
  label: string,
  body: string
): { pem: string; companyId: string | null } {
  const normalizedBody = normalizePemBody(body);
  if (isPkcsBase64(normalizedBody)) {
    return {
      pem: buildPem("CERTIFICATE", normalizedBody),
      companyId: null,
    };
  }
  const unwrapped = tryUnwrapSapDoubleBase64(normalizedBody);
  if (unwrapped) {
    return {
      pem: buildPem("CERTIFICATE", unwrapped.innerBase64),
      companyId: companyIdFromSuffix(unwrapped.suffix),
    };
  }
  throw new SuccessFactorsCertificateParseError(
    "Could not read the X.509 certificate from this file. Download the OAuth certificate from SAP again and upload it without editing."
  );
}

function resolvePrivateKeyPem(
  label: string,
  body: string
): { pem: string; companyId: string | null } {
  const normalizedLabel = label.trim();
  const normalizedBody = normalizePemBody(body);

  if (
    normalizedLabel === "PRIVATE KEY" ||
    normalizedLabel === "RSA PRIVATE KEY"
  ) {
    if (isPkcsBase64(normalizedBody)) {
      const outputLabel =
        normalizedLabel === "RSA PRIVATE KEY"
          ? "RSA PRIVATE KEY"
          : "PRIVATE KEY";
      return {
        pem: buildPem(outputLabel, normalizedBody),
        companyId: null,
      };
    }
  }

  if (normalizedLabel === "ENCRYPTED PRIVATE KEY") {
    const unwrapped = tryUnwrapSapDoubleBase64(normalizedBody);
    if (unwrapped && !isEncryptedPkcs8Base64(unwrapped.innerBase64)) {
      return {
        pem: buildPem("PRIVATE KEY", unwrapped.innerBase64),
        companyId: companyIdFromSuffix(unwrapped.suffix),
      };
    }
    throw new SuccessFactorsCertificateParseError(
      PASSPHRASE_PROTECTED_KEY_MESSAGE
    );
  }

  if (isPkcsBase64(normalizedBody)) {
    return {
      pem: buildPem("PRIVATE KEY", normalizedBody),
      companyId: null,
    };
  }

  throw new SuccessFactorsCertificateParseError(
    "Could not read the private key from this file. Download the OAuth certificate from SAP again and upload it without editing."
  );
}

export function parseSuccessFactorsCertificateFile(
  text: string
): ParsedSuccessFactorsCertificateFile {
  const blocks = [...text.matchAll(PEM_BLOCK_PATTERN)];
  if (blocks.length === 0) {
    throw new SuccessFactorsCertificateParseError(
      "This file does not contain a PEM certificate and private key. Upload the certificate file downloaded from SAP SuccessFactors OAuth client settings."
    );
  }

  let certificatePem = "";
  let privateKeyPem = "";
  let companyId: string | null = null;

  for (const block of blocks) {
    const label = block[1];
    const body = block[2];
    if (label === "CERTIFICATE") {
      const resolved = resolveCertificatePem(label, body);
      certificatePem = resolved.pem;
      companyId = companyId ?? resolved.companyId;
      continue;
    }
    if (
      label === "PRIVATE KEY" ||
      label === "RSA PRIVATE KEY" ||
      label === "ENCRYPTED PRIVATE KEY"
    ) {
      const resolved = resolvePrivateKeyPem(label, body);
      privateKeyPem = resolved.pem;
      if (resolved.companyId) {
        companyId = resolved.companyId;
      }
    }
  }

  if (!certificatePem || !privateKeyPem) {
    throw new SuccessFactorsCertificateParseError(
      "This file must include both an X.509 certificate and a private key. Upload the complete certificate file from SAP without opening or editing it."
    );
  }

  return { certificatePem, privateKeyPem, companyId };
}
